import { ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { DeviceObservationAttributes } from '@csp/device-catalog';
import { CustomStatusV1, DeviceObservationV1, ValueV1 } from '@csp/dmdp-api-device-dto';
import { InvalidatedByPatientReason, Maybe } from '@csp/csp-common-model';
import { DeviceObservation, DeviceObservationValue } from '../model/DeviceObservation';
import { DeviceObservationRequestRef } from '../model/DeviceObservationRequestRef';
import { DeviceObservationStatus } from '../model/DeviceObservationStatus';
import { DeviceObservationRefs } from './DeviceObservationRefs';

const toDeviceObservationStatus = (status?: string): DeviceObservationStatus => {
  switch (status) {
    case 'UNSCHEDULED':
      return 'UNSCHEDULED';
    case 'SCHEDULED':
      return 'SCHEDULED';
    case 'MISSED':
      return 'MISSED';
    default:
      return 'MISSED';
  }
};

const isDefinedDeviceObservation = (value: ValueV1): boolean => !!DeviceObservationAttributes[value.name];

const deviceObservationValuesOf = (values: ValueV1[]): DeviceObservationValue[] =>
  values.filter(isDefinedDeviceObservation).map(value => value as DeviceObservationValue);

const toInvalidatedByPatientReason = (customStatuses?: CustomStatusV1[]): Maybe<InvalidatedByPatientReason> =>
  customStatuses?.find(status => status.type === 'invalidatedByPatient')?.reason as InvalidatedByPatientReason;

const from = ({
  customStatuses,
  deviceObservationId,
  eventId,
  refs = [],
  scheduleRequestRef,
  scheduleRequestStatus,
  sensorTimestamp: sensorTimestampStr,
  sessionCode,
  synchronizationTimestamp,
  userDeviceId,
  userId,
  values = [],
  invalidated,
  audit,
  nonConsentData,
}: DeviceObservationV1): DeviceObservation => {
  const status = toDeviceObservationStatus(scheduleRequestStatus);

  return {
    correlationId: DeviceObservationRefs.getCorrelationId(refs),
    eventId,
    id: deviceObservationId,
    missed: status === 'MISSED',
    observationType: DeviceObservationRefs.getObservationType(refs),
    scheduleRequestRef: DeviceObservationRequestRef.from(scheduleRequestRef),
    sensorTimestamp: sensorTimestampStr ? ZonedDateTimeFactory.fromZonedDateTimeString(sensorTimestampStr) : undefined,
    sessionCode,
    status,
    synchronizationTimestamp: ZonedDateTimeFactory.fromZonedDateTimeString(synchronizationTimestamp),
    userDeviceId,
    userId,
    values: deviceObservationValuesOf(values),
    isObservationInvalidated: invalidated,
    invalidatedByPatientReason: toInvalidatedByPatientReason(customStatuses),
    hasConsentData: !nonConsentData,
    createdAt: audit?.createdAt ? ZonedDateTimeFactory.fromZonedDateTimeString(audit?.createdAt) : undefined,
  };
};

export const DeviceObservationMapper = {
  from,
};
